/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import { createContext, useContext, useEffect, useState, useRef } from 'react'
import { useAuth } from './AuthContext'
import { getProfiles, getProfileBrands, addProfileBrand, removeProfileBrand, createProfile, deleteProfile, rateProfileBrand, unrateProfileBrand } from '../api'

const ProfileContext = createContext({})

export function ProfileProvider({ children }) {
    const { user } = useAuth()
    const [profiles, setProfiles] = useState([])
    const [activeProfileId, setActiveProfileId] = useState(null)
    const [profileBrands, setProfileBrands] = useState([])
    const [loading, setLoading] = useState(false)
    // Increments every time a NEW profile's brands finish loading
    const [brandsLoadVersion, setBrandsLoadVersion] = useState(0)
    // Tracks which profile ID the current profileBrands belong to
    const [loadedForProfileId, setLoadedForProfileId] = useState(null)
    // Ref to track the latest requested profile (race condition guard)
    const latestRequestedProfileId = useRef(null)

    useEffect(() => {
        if (!user) {
            setProfiles([])
            setActiveProfileId(null)
            setProfileBrands([])
            setLoadedForProfileId(null)
            return
        }
        loadProfiles()
    }, [user])

    useEffect(() => {
        if (!activeProfileId) {
            setProfileBrands([])
            setLoadedForProfileId(null)
            setBrandsLoadVersion(v => v + 1)
            return
        }
        loadProfileBrandsForSwitch(activeProfileId)
    }, [activeProfileId])

    const hasInitialized = useRef(false)

    const loadProfiles = async () => {
        if (!user) return
        setLoading(true)
        try {
            const data = await getProfiles(user.id)
            setProfiles(data || [])
            const defaultProfile = (data || []).find(p => p.is_default)
            // Only auto-set default profile on first load — don't override scratch mode
            if (defaultProfile && !hasInitialized.current) {
                hasInitialized.current = true
                const lastProfile = localStorage.getItem('marqueindex_last_profile')
                // If user previously chose scratch, respect that
                if (lastProfile === '__scratch__') {
                    setActiveProfileId(null)
                } else if (lastProfile && lastProfile !== '__scratch__') {
                    setActiveProfileId(lastProfile)
                } else {
                    setActiveProfileId(defaultProfile.id)
                }
            } else {
                hasInitialized.current = true
            }
        } catch (e) {
            console.error('Error loading profiles:', e)
        }
        setLoading(false)
    }

    const loadProfileBrandsForSwitch = async (profileId) => {
        // Immediately clear old data so UI doesn't show stale brands
        setProfileBrands([])
        setLoadedForProfileId(null)
        setLoading(true)

        // Track which profile we're loading for (race condition guard)
        latestRequestedProfileId.current = profileId

        try {
            const data = await getProfileBrands(profileId)

            // Only apply if this is still the profile we want
            // (user may have switched again while fetch was in flight)
            if (latestRequestedProfileId.current !== profileId) {
                return
            }

            setProfileBrands(data || [])
            setLoadedForProfileId(profileId)
            setBrandsLoadVersion(v => v + 1)
        } catch (e) {
            console.error('Error loading profile brands:', e)
        }

        // Only clear loading if this is still the active request
        if (latestRequestedProfileId.current === profileId) {
            setLoading(false)
        }
    }

    const activeProfile = profiles.find(p => p.id === activeProfileId)
    const defaultProfile = profiles.find(p => p.is_default)
    const customProfiles = profiles.filter(p => !p.is_default)
    const customProfile = customProfiles[0] || null
    const canCreateCustomProfile = customProfiles.length < 2

    const likedBrandIds = profileBrands
        .filter(pb => pb.type === 'liked')
        .map(pb => pb.brand_id)

    const dislikedBrandIds = profileBrands
        .filter(pb => pb.type === 'disliked')
        .map(pb => pb.brand_id)

    // Map of brand_id → rating (1-5 or null)
    const brandRatings = Object.fromEntries(
        profileBrands
            .filter(pb => pb.rating != null)
            .map(pb => [pb.brand_id, pb.rating])
    )

    const getBrandRating = (brandId) => brandRatings[brandId] || null

    const setRating = async (brandId, rating) => {
        if (!activeProfileId) return
        setProfileBrands(prev => prev.map(pb =>
            pb.brand_id === brandId ? { ...pb, rating } : pb
        ))
        if (rating === null) {
            await unrateProfileBrand(activeProfileId, brandId)
        } else {
            await rateProfileBrand(activeProfileId, brandId, rating)
        }
    }

    const isSaved = (brandId) => likedBrandIds.includes(brandId)
    const isDisliked = (brandId) => dislikedBrandIds.includes(brandId)

    const toggleSave = async (brandId) => {
        const profileId = activeProfileId || defaultProfile?.id
        if (!profileId) return
        if (isSaved(brandId)) {
            setProfileBrands(prev => prev.filter(pb => !(pb.brand_id === brandId && pb.type === 'liked')))
            await removeProfileBrand(profileId, brandId)
        } else {
            if (isDisliked(brandId)) {
                setProfileBrands(prev => prev.filter(pb => !(pb.brand_id === brandId)))
            }
            setProfileBrands(prev => [...prev, { brand_id: brandId, type: 'liked', profile_id: profileId }])
            await addProfileBrand(profileId, brandId, 'liked')
        }
    }

    const toggleDislike = async (brandId) => {
        const profileId = activeProfileId || defaultProfile?.id
        if (!profileId) return
        if (isDisliked(brandId)) {
            setProfileBrands(prev => prev.filter(pb => !(pb.brand_id === brandId && pb.type === 'disliked')))
            await removeProfileBrand(profileId, brandId)
        } else {
            if (isSaved(brandId)) {
                setProfileBrands(prev => prev.filter(pb => !(pb.brand_id === brandId)))
            }
            setProfileBrands(prev => [...prev, { brand_id: brandId, type: 'disliked', profile_id: profileId }])
            await addProfileBrand(profileId, brandId, 'disliked')
        }
    }

    const saveBrandsToProfile = async (brandIds) => {
        if (!activeProfileId) return
        for (const brandId of brandIds) {
            if (!isSaved(brandId)) {
                setProfileBrands(prev => [...prev, { brand_id: brandId, type: 'liked', profile_id: activeProfileId }])
                await addProfileBrand(activeProfileId, brandId, 'liked')
            }
        }
    }

    const handleCreateProfile = async (name) => {
        if (!user) return null
        try {
            const result = await createProfile(user.id, name)
            await loadProfiles()
            if (result.profile) {
                setActiveProfileId(result.profile.id)
                // Force reload brands for new profile (will be empty but clears stale state)
                await loadProfileBrandsForSwitch(result.profile.id)
            }
            return result
        } catch (e) {
            throw e
        }
    }

    const handleDeleteProfile = async (profileId) => {
        try {
            await deleteProfile(profileId)
            if (defaultProfile) {
                setActiveProfileId(defaultProfile.id)
            }
            await loadProfiles()
        } catch (e) {
            console.error('Error deleting profile:', e)
        }
    }

    const switchProfile = (profileId) => {
        if (profileId === activeProfileId) return
        setActiveProfileId(profileId)
    }

    return (
        <ProfileContext.Provider value={{
            profiles,
            activeProfile,
            activeProfileId,
            defaultProfile,
            customProfile,
            customProfiles,
            canCreateCustomProfile,
            likedBrandIds,
            dislikedBrandIds,
            isSaved,
            isDisliked,
            toggleSave,
            toggleDislike,
            saveBrandsToProfile,
            switchProfile,
            createProfile: handleCreateProfile,
            deleteProfile: handleDeleteProfile,
            reloadProfileBrands: loadProfileBrandsForSwitch,
            brandRatings,
            getBrandRating,
            setRating,
            brandsLoadVersion,
            loadedForProfileId,
            loading
        }}>
            {children}
        </ProfileContext.Provider>
    )
}

export const useProfiles = () => useContext(ProfileContext)
